import { useState } from 'react'
import { Button, ActionIcon, Drawer, Modal } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import {
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
} from '@tabler/icons-react'
import PeriodPicker from './PeriodPicker'
import classes from './PeriodPicker.module.css'
import {
  formatQuarterLabel,
  quarterOf,
  quarterStart,
  yearOf,
} from '../../widgets/quarter'

const monthNames = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

interface PeriodSelectProps {
  start_month: string
  end_month: string
  periodType: 'total' | 'month' | 'quarter' | 'year'
  period: string
  onChange: (periodType: string, period: string) => void
}

// Helper function to convert timestamp/ISO date to YYYY-MM-01 format
function formatToFirstOfMonth(dateString: string): string {
  const date = new Date(dateString)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  return `${year}-${month}-01`
}

function formatPeriodLabel(periodType: string, period: string): string {
  if (periodType === 'total') {
    return 'Total'
  } else if (periodType === 'year') {
    // Extract year from period like "2025-01-01"
    const year = period.split('-')[0]
    return year
  } else if (periodType === 'quarter') {
    return formatQuarterLabel(period)
  } else if (periodType === 'month') {
    // Extract month and year from period like "2025-01-01"
    const [year, month] = period.split('-')
    const monthIndex = parseInt(month, 10) - 1
    return `${monthNames[monthIndex]} ${year}`
  }
  return 'Total'
}

function calculatePrevPeriod(
  periodType: string,
  period: string
): { periodType: string; period: string } | null {
  if (periodType === 'total') return null

  if (periodType === 'month') {
    const date = new Date(period)
    date.setMonth(date.getMonth() - 1)
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    return { periodType: 'month', period: `${year}-${month}-01` }
  } else if (periodType === 'quarter') {
    const q = quarterOf(period)
    const year = yearOf(period)
    const prev = q === 1 ? quarterStart(year - 1, 4) : quarterStart(year, q - 1)
    return { periodType: 'quarter', period: prev }
  } else if (periodType === 'year') {
    const year = parseInt(period.split('-')[0], 10)
    return { periodType: 'year', period: `${year - 1}-01-01` }
  }

  return null
}

function calculateNextPeriod(
  periodType: string,
  period: string
): { periodType: string; period: string } | null {
  if (periodType === 'total') return null

  if (periodType === 'month') {
    const date = new Date(period)
    date.setMonth(date.getMonth() + 1)
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    return { periodType: 'month', period: `${year}-${month}-01` }
  } else if (periodType === 'quarter') {
    const q = quarterOf(period)
    const year = yearOf(period)
    const next = q === 4 ? quarterStart(year + 1, 1) : quarterStart(year, q + 1)
    return { periodType: 'quarter', period: next }
  } else if (periodType === 'year') {
    const year = parseInt(period.split('-')[0], 10)
    return { periodType: 'year', period: `${year + 1}-01-01` }
  }

  return null
}

export default function PeriodSelect({
  start_month,
  end_month,
  periodType,
  period,
  onChange,
}: PeriodSelectProps) {
  const [modalOpened, setModalOpened] = useState(false)
  const isDesktop = useMediaQuery('(min-width: 800px)')

  const buttonLabel = formatPeriodLabel(periodType, period)

  // Calculate prev/next periods
  const prevPeriod = calculatePrevPeriod(periodType, period)
  const nextPeriod = calculateNextPeriod(periodType, period)

  // Check if prev/next are within competition boundaries
  const competitionStart = formatToFirstOfMonth(start_month)
  const competitionEnd = formatToFirstOfMonth(end_month)

  const canGoPrev = prevPeriod ? prevPeriod.period >= competitionStart : false
  const canGoNext = nextPeriod ? nextPeriod.period <= competitionEnd : false

  const handlePrevPeriod = () => {
    if (prevPeriod && canGoPrev) {
      onChange(prevPeriod.periodType, prevPeriod.period)
    }
  }

  const handleNextPeriod = () => {
    if (nextPeriod && canGoNext) {
      onChange(nextPeriod.periodType, nextPeriod.period)
    }
  }

  const handlePeriodChange = (newPeriodType: string, newPeriod: string) => {
    onChange(newPeriodType, newPeriod)
    setModalOpened(false)
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
      <ActionIcon
        variant="default"
        onClick={handlePrevPeriod}
        disabled={!canGoPrev}
        style={{
          border: '1px solid var(--mantine-color-gray-6)',
          backgroundColor: 'light-dark(white, var(--mantine-color-gray-9)',
          width: '36px',
          height: '36px',
          color:
            'light-dark(var(--mantine-color-gray-6), var(--mantine-color-gray-4))',
        }}
      >
        <IconChevronLeft size={16} />
      </ActionIcon>

      <Button
        variant="default"
        rightSection={<IconChevronDown size={16} />}
        onClick={() => setModalOpened(true)}
        styles={{
          root: {
            border: '0.5px solid var(--mantine-color-gray-6)',
            color:
              'light-dark(var(--mantine-color-gray-6), var(--mantine-color-gray-4))',
            fontSize: '14px',
            fontWeight: 400,
            padding: '6px 12px',
            backgroundColor: 'light-dark(white, var(--mantine-color-gray-9)',
          },
        }}
      >
        {buttonLabel}
      </Button>

      <ActionIcon
        variant="default"
        onClick={handleNextPeriod}
        disabled={!canGoNext}
        style={{
          border: '1px solid var(--mantine-color-gray-6)',
          backgroundColor: 'light-dark(white, var(--mantine-color-gray-9)',
          width: '36px',
          height: '36px',
          color:
            'light-dark(var(--mantine-color-gray-6), var(--mantine-color-gray-4))',
        }}
      >
        <IconChevronRight size={16} />
      </ActionIcon>

      {isDesktop ? (
        <Modal.Root
          opened={modalOpened}
          onClose={() => setModalOpened(false)}
          centered
          size="md"
          transitionProps={{
            transition: 'fade',
            duration: 200,
            exitDuration: 150,
            timingFunction: 'ease-out',
          }}
          classNames={{
            header: classes.modalHeader,
            body: classes.modalBody,
          }}
        >
          <Modal.Overlay backgroundOpacity={0.3} blur={2} />
          <Modal.Content>
            <Modal.Header>
              <Modal.Title>Select Period</Modal.Title>
              <Modal.CloseButton />
            </Modal.Header>
            <Modal.Body>
              <PeriodPicker
                start_month={competitionStart}
                end_month={competitionEnd}
                periodType={periodType}
                period={period}
                onChange={handlePeriodChange}
              />
            </Modal.Body>
          </Modal.Content>
        </Modal.Root>
      ) : (
        <Drawer.Root
          opened={modalOpened}
          onClose={() => setModalOpened(false)}
          position="bottom"
          transitionProps={{
            transition: 'fade',
            duration: 200,
            exitDuration: 150,
            timingFunction: 'ease-out',
          }}
          classNames={{
            header: classes.drawerHeader,
            body: classes.drawerBody,
            content: classes.drawerContent,
          }}
        >
          <Drawer.Overlay backgroundOpacity={0.3} blur={2} />
          <Drawer.Content>
            <Drawer.Header>
              <Drawer.Title>Select Period</Drawer.Title>
              <Drawer.CloseButton />
            </Drawer.Header>
            <Drawer.Body>
              <PeriodPicker
                start_month={competitionStart}
                end_month={competitionEnd}
                periodType={periodType}
                period={period}
                onChange={handlePeriodChange}
              />
            </Drawer.Body>
          </Drawer.Content>
        </Drawer.Root>
      )}
    </div>
  )
}
